// Links students send to friends: "Share Re:Vault" and Study Hall room invites.
//
// The link uses the address the student is on right now. Most students reach Re:Vault through a
// school-friendly address of their own because endis.rest is often blocked at school, and a friend
// at the same school is behind the same filter, so the address that works for one usually works for
// the other. A few hosts are never handed out and share https://endis.rest instead: the sandbox
// origin where proxied pages run (view.* or whatever label the build picked), beta hosts (beta.*),
// bare IP addresses and anything not on https. A local test server shares itself so the checks can
// follow the link. The `s` parameter only says how someone arrived ("share" or "invite"); the
// visitor counter adds it to the day's totals and the app drops it from the address. No ids,
// nothing about who shared.
import { isSandboxLabelHost } from "../../server/lib/sandbox-roles.js";

export const SHARE_ORIGIN = "https://endis.rest";
export const SHARE_TITLE = "Share Re:Vault";
export const SHARE_TEXT = "Studying with friends is better. Send them Re:Vault.";
// What a share sheet or a chat app shows next to the link.
export const SHARE_MESSAGE = "I study with Re:Vault: books, study tools and focus rooms in one place.";

const LOCAL = /^(?:localhost|127(?:\.\d{1,3}){3}|\[::1\]|(?:[a-z0-9-]+\.)+localhost)$/i;
const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
// A dotted DNS name: at least two labels, letters, digits and hyphens only.
const NAME = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{0,62}$/i;

// The sandbox label comes from the build (.rvprefixes.json). The tests import this file without a
// build, so it falls back to the default there.
let LABEL = "view";
try { LABEL = __RVM.sandboxLabel || LABEL; } catch { /* not built */ }

// True when a host is fine to put in a link for someone else.
export function shareableHost(hostname, label = LABEL) {
	const h = String(hostname || "").toLowerCase().replace(/\.$/, "");
	if (!h || IPV4.test(h) || h.includes(":") || h.startsWith("[") || !NAME.test(h)) return false;
	const first = h.split(".")[0];
	if (first === "beta") return false;
	if (first === "view" || isSandboxLabelHost(h, label)) return false;
	return true;
}

export function shareOrigin(loc) {
	try {
		if (!loc) return SHARE_ORIGIN;
		const host = String(loc.hostname);
		if (LOCAL.test(host) && /^https?:$/.test(loc.protocol)) return loc.origin;
		if (loc.protocol === "https:" && shareableHost(host) && /^https:\/\/[^/]+$/.test(String(loc.origin))) return loc.origin;
	} catch { /* fall through */ }
	return SHARE_ORIGIN;
}
export const shareUrl = (loc) => shareOrigin(loc) + "/?s=share";

// Invite codes: 10 characters, no 0/O/1/I so they survive being read out loud.
export const INVITE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const INVITE_RE = /^[A-HJ-NP-Z2-9]{10}$/;
// The code rides in the #fragment, which browsers never send to a server, so it stays out of
// access logs (a log line with the code and an address could be matched to whoever made it).
// Codes are checked by the one Study Hall server behind every address, so an invite made on one
// address opens on any other.
export const inviteUrl = (loc, code) => shareOrigin(loc) + "/?s=invite#invite=" + code;

// A code from an address (#invite=CODE), upper-cased; "" when there is none or it's malformed.
export function inviteFrom(hash) {
	const m = /^#invite=([^&]*)$/i.exec(String(hash || ""));
	const v = m ? m[1].trim().toUpperCase() : "";
	return INVITE_RE.test(v) ? v : "";
}

// The address without the arrival parameters, for history.replaceState.
export function withoutArrival(loc) {
	let q;
	try { q = new URLSearchParams(loc.search); } catch { return loc.pathname; }
	q.delete("s");
	const s = q.toString();
	return loc.pathname + (s ? "?" + s : "") + (/^#invite=/i.test(loc.hash || "") ? "" : loc.hash);
}
